/**
 * InstallAppCTA — single big "Install the app" button that auto-detects the
 * user's device and triggers the right install flow.
 * Iter 69.
 *
 * Behaviour on click:
 *   • Android Chrome/Edge with the `beforeinstallprompt` event already
 *     captured → fire prompt() immediately (one-tap install).
 *   • Android without prompt (Firefox or already shown) → modal with the
 *     "menu → Install app" steps.
 *   • iPhone / iPad Safari → modal with the Share-icon → "Add to Home Screen"
 *     instructions and a visual mock-up so non-technical patients can follow.
 *   • Desktop → modal explaining this is a mobile-first app + QR code link.
 *
 * Renders nothing when the app is already running standalone (i.e. installed),
 * to avoid nagging installed users.
 */
import React, { useEffect, useState } from 'react';
import { Smartphone, Apple, Share, Plus, Download, ChevronUp, Info, MoreVertical, ArrowUp, X } from 'lucide-react';

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export default function InstallAppCTA({ variant = 'solo' }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [modal, setModal] = useState(null);  // null | 'android' | 'ios' | 'desktop' | 'success'
  const platform = detectPlatform();

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setModal('success'); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  const onInstallClick = async () => {
    if (deferred) {
      // Android Chrome/Edge with deferred prompt — fire it directly.
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setModal('success');
      setDeferred(null);
      return;
    }
    // No prompt available — open the right modal for the user's device.
    if (platform === 'ios') setModal('ios');
    else if (platform === 'android') setModal('android');
    else setModal('desktop');
  };

  return (
    <section className="px-6 md:px-12 py-14 bg-bg-secondary/40 border-y border-soft" data-testid={`install-app-${variant}`}>
      <div className="max-w-4xl mx-auto text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-[#003D80] font-bold mb-3">
          {variant === 'multi' ? 'FOR EVERY CLINIC' : 'FOR PATIENTS & STAFF'}
        </div>
        <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3 tracking-tight text-[#0F1B2D]">
          Get the app on your phone
        </h2>
        <p className="text-fg-secondary max-w-2xl mx-auto mb-6 text-base">
          {variant === 'multi'
            ? 'Your patients and staff install it in one tap — works fullscreen like a native app, no app store needed.'
            : 'Add Physiogenic to your home screen to book, message your therapist, and check your training program in one tap. Works offline.'}
        </p>

        <p className="text-base md:text-lg text-fg leading-relaxed font-medium mb-6" data-testid="install-marketing">
          <strong className="text-[#003D80]">Free</strong> · <strong className="text-[#003D80]">No app store</strong> ·{' '}
          <strong className="text-[#003D80]">Updates automatically</strong>
        </p>

        {/* Single big install button — auto-detects device, fires prompt or modal. */}
        <button onClick={onInstallClick}
                data-testid="install-app-cta-button"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-white font-bold text-lg shadow-[0_10px_30px_rgba(0,61,128,0.25)] hover:shadow-[0_14px_40px_rgba(0,61,128,0.35)] hover:-translate-y-0.5 transition"
                style={{ background: '#003D80' }}>
          <Download className="w-5 h-5"/>
          {deferred ? 'Install on this device' : 'Install the app'}
        </button>

        <div className="flex items-center justify-center gap-5 mt-5 text-xs text-fg-muted">
          <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5"/> Android 8+</span>
          <span className="inline-flex items-center gap-1.5"><Apple className="w-3.5 h-3.5"/> iOS 16+</span>
          <span className="inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5"/> No store required</span>
        </div>
      </div>

      {modal === 'android' && <AndroidModal onClose={() => setModal(null)}/>}
      {modal === 'ios' && <IosModal onClose={() => setModal(null)}/>}
      {modal === 'desktop' && <DesktopModal onClose={() => setModal(null)}/>}
      {modal === 'success' && <SuccessModal onClose={() => setModal(null)}/>}
    </section>
  );
}

function ModalShell({ icon: Icon, title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-soft flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#003D80] text-white flex items-center justify-center">
              <Icon className="w-4 h-4"/>
            </div>
            <h3 className="font-heading font-bold text-[#0F1B2D]">{title}</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg" data-testid={`${testid}-close`}>
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function IosModal({ onClose }) {
  return (
    <ModalShell icon={Apple} title="Install on iPhone / iPad" onClose={onClose} testid="install-ios-modal">
      <p className="text-sm text-fg-secondary mb-4">
        Apple's Safari doesn't show an install prompt — but it only takes <strong>10 seconds</strong> to add Physiogenic to your home screen:
      </p>
      <ol className="space-y-3.5 text-sm">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">1</span>
          <span>Open this page in <strong>Safari</strong> (not Chrome — Apple only allows installs from Safari).</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">2</span>
          <span>Tap the <strong>Share</strong> icon <Share className="w-3.5 h-3.5 inline-block mb-0.5 text-[#003D80]"/> at the bottom of the screen.</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">3</span>
          <span>Scroll down and tap <strong>"Add to Home Screen"</strong>.</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">4</span>
          <span>Tap <strong>Add</strong> in the top-right corner. The icon appears on your home screen ✨</span>
        </li>
      </ol>
      <div className="mt-4 p-3 rounded-lg bg-[#FFF9EC] text-[12px] text-[#7A5500] flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5"/>
        <span>If you don't see "Add to Home Screen" in the Share sheet, scroll down — it sometimes hides under "More Actions".</span>
      </div>
    </ModalShell>
  );
}

function AndroidModal({ onClose }) {
  return (
    <ModalShell icon={Smartphone} title="Install on Android" onClose={onClose} testid="install-android-modal">
      <p className="text-sm text-fg-secondary mb-4">
        Your browser didn't auto-prompt this time, but you can still install in <strong>5 seconds</strong>:
      </p>
      <ol className="space-y-3.5 text-sm">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">1</span>
          <span>Open this page in <strong>Chrome</strong> or <strong>Edge</strong> (Firefox doesn't support it).</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">2</span>
          <span>Tap the browser menu <MoreVertical className="w-3.5 h-3.5 inline-block mb-0.5 text-[#003D80]"/> (top-right).</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">3</span>
          <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">4</span>
          <span>Confirm — Physiogenic is now installed like a native app.</span>
        </li>
      </ol>
    </ModalShell>
  );
}

function DesktopModal({ onClose }) {
  const url = typeof window !== 'undefined' ? window.location.origin : 'https://physiogenic.app';
  return (
    <ModalShell icon={Smartphone} title="The app is best on mobile" onClose={onClose} testid="install-desktop-modal">
      <p className="text-sm text-fg-secondary mb-4">
        Physiogenic works in any browser, but the install option is designed for phones and tablets. To install on your device:
      </p>
      <ol className="space-y-3 text-sm mb-4">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">1</span>
          <span>On your phone, open the browser and visit:</span>
        </li>
      </ol>
      <div className="bg-[#FFF9EC] p-3 rounded-lg text-center mb-3">
        <code className="text-sm font-mono text-[#003D80] break-all">{url}</code>
      </div>
      <p className="text-xs text-fg-muted">Then tap "Install the app" again — your browser will guide you through it.</p>
    </ModalShell>
  );
}

function SuccessModal({ onClose }) {
  return (
    <ModalShell icon={Plus} title="You're all set! 🎉" onClose={onClose} testid="install-success-modal">
      <p className="text-sm text-fg-secondary">
        Physiogenic has been added to your home screen. Look for the Physiogenic icon — tap it any time to open the app fullscreen.
      </p>
      <button onClick={onClose} className="w-full mt-4 btn-primary text-sm">Got it</button>
    </ModalShell>
  );
}
